import React, { cloneElement } from 'react';
import useMediaQuery from '@mui/material/useMediaQuery';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import MoreVertIcon from '@mui/icons-material/MoreVert';

export type ActionMenuItem = {
  icon: React.ReactNode;
  label: string;
  onClick?: () => void;
};

type ResponsiveActionMenuProps = {
  actions: ActionMenuItem[];
  iconSize?: number;
  /**
   * Largeur max (en px) à partir de laquelle le composant passe en mode mobile. Par défaut 600.
   */
  mobileBreakpoint?: number;
};

const ResponsiveActionMenu: React.FC<ResponsiveActionMenuProps> = ({
  actions,
  iconSize = 24,
  mobileBreakpoint = 600,
}) => {
  const isMobile = useMediaQuery(`(max-width:${mobileBreakpoint}px)`);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  if (!isMobile) {
    // Desktop/tablette : toutes les icônes visibles
    return (
      <div style={{ display: 'flex', flexDirection: 'row', gap: 18 }}>
        {actions.map((action, idx) => (
          <Tooltip title={action.label} key={action.label}>
            <span>
              <IconButton onClick={action.onClick} size="large" style={{ width: iconSize, height: iconSize }}>
                {action.icon}
              </IconButton>
            </span>
          </Tooltip>
        ))}
      </div>
    );
  }

  // Mobile : menu déroulant
  return (
    <div>
      <IconButton onClick={handleMenuOpen} size="large">
        <MoreVertIcon style={{ width: iconSize, height: iconSize }} />
      </IconButton>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        {actions.map((action) => (
          <MenuItem
            key={action.label}
            onClick={(e: React.MouseEvent<HTMLElement>) => {
              e.preventDefault();
              action?.onClick && action.onClick();
              // setTimeout pour éviter que le menu se ferme avant que l'action soit effectuée
              setTimeout(handleMenuClose, 200);
            }}
            style={{ padding: '10px 20px' }}
          >
            <span
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                minWidth: 32,
              }}
            >
              <span style={{ display: 'flex', alignItems: 'center', width: 28, height: 28 }}>
                {React.isValidElement(action.icon)
                  ? cloneElement(action.icon as React.ReactElement<{ style: React.CSSProperties }>, {
                      style: { fontSize: 24, color: '#222' },
                    })
                  : action.icon}
              </span>
              <span style={{ fontSize: 17, color: '#222', fontWeight: 500 }}>{action.label}</span>
            </span>
          </MenuItem>
        ))}
      </Menu>
    </div>
  );
};

export default ResponsiveActionMenu;
